/* OVESSI skin-reading bridge v9 — input-hook questionnaire harvest
 *
 * v9 changes vs v8:
 *   - The FaceAge vendor build (a) never exposes the instance through
 *     window.FaceAge (so the ctor patch never fires) and (b) destroys the
 *     questionnaire DOM before tryCapture runs. So we cannot read the answers
 *     either from instance.formData or from the iframe DOM at capture time.
 *   - v9 installs a SYNCHRONOUS input/change/click LISTENER on the parent
 *     window AND on the FaceAge iframe contentDocument the moment that iframe
 *     exists. Each event classifies the target by name/id/placeholder/aria/label
 *     and PERSISTS the meaningful value to localStorage IMMEDIATELY (so even if
 *     the DOM is torn down a millisecond later, the value is safe).
 *   - tryCapture then reads from localStorage at deliver time. The DOM-read
 *     and instance-read paths are kept as ultra-low-priority fallbacks.
 *   - Also writes a structured snapshot to localStorage.ovssc3_questionnaire
 *     containing every observed answer keyed by field-id.
 *   - All v7/v8 behaviour preserved: merge persistReading, DOM scrape fallback,
 *     lifestyle harvest, skinAnalysis sweep, gender/skin-tone stage labels.
 */
(function () {
  "use strict";
  if (window.__ovssc_dom_bridge_booted) return;
  window.__ovssc_dom_bridge_booted = true;
  window.__ovssc_dom_bridge_version = "dom-v11-2026-06-26-sole-bridge";

  /* ---------------- FaceAge constructor patch (v8) ----------------
   * Patches both the parent window and the FaceAge iframe contentWindow,
   * polling every 100ms because the vendor script can load at any time and
   * the iframe may not exist yet. Once captured, window.__ovssc_faceAge_instance
   * exposes the live FaceAge instance with .w.globals.formData and
   * .w.globals.skinAnalyzeData populated as the user progresses. */
  function patchFaceAgeOn(win, label) {
    try {
      if (!win || win.__ovssc_faceAge_patched) return false;
      if (typeof win.FaceAge !== "function") return false;
      var Original = win.FaceAge;
      function Patched(opts) {
        var inst = new Original(opts);
        try {
          window.__ovssc_faceAge_instance = inst;
          window.__ovssc_faceAge_instance_source = label;
        } catch (_) {}
        return inst;
      }
      Patched.prototype = Original.prototype;
      try {
        Object.keys(Original).forEach(function (k) {
          try { Patched[k] = Original[k]; } catch (_) {}
        });
      } catch (_) {}
      try { win.FaceAge = Patched; } catch (_) {}
      try { win.__ovssc_faceAge_patched = true; } catch (_) {}
      try { console.log("[ovssc-dom v8] patched FaceAge on " + label); } catch (_) {}
      return true;
    } catch (e) { return false; }
  }
  (function tryPatchLoop() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var patchedParent = patchFaceAgeOn(window, "parent");
      var iframe = null;
      try {
        iframe = document.getElementById("FaceAge-module-iframe")
              || document.querySelector("#faceAge-module iframe")
              || document.querySelector("iframe[id*='FaceAge']")
              || document.querySelector("iframe[src*='faceage']")
              || document.querySelector("iframe[src*='FaceAge']");
      } catch (_) {}
      var iwin = null;
      try { iwin = iframe && iframe.contentWindow; } catch (_) {}
      var patchedIframe = patchFaceAgeOn(iwin, "iframe");
      // Stop after 5 minutes regardless
      if (tries > 3000) { try { clearInterval(iv); } catch (_) {} }
    }, 100);
  })();

  /* ---------------- v9 input-hook ----------------------------------
   * Synchronously listens for every input/change/click on the parent
   * window AND inside the FaceAge iframe. As soon as a field is
   * recognised, the value is persisted to localStorage so the bridge
   * can read it later even after the FaceAge widget tears the DOM down.
   *
   * Persisted keys:
   *   ovssc3_first_name, ovssc3_email, ovssc3_age, ovssc3_gender,
   *   ovssc3_skin_tone, ovssc3_questionnaire (JSON blob of all observed
   *   fields keyed by best-guess id).
   */
  var Q_KEY = "ovssc3_questionnaire";
  function lsGetJSON(k) {
    try {
      var raw = localStorage.getItem(k);
      if (!raw) return {};
      var p = JSON.parse(raw);
      return (p && typeof p === "object") ? p : {};
    } catch (_) { return {}; }
  }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function lsSetJSON(k, obj) { try { localStorage.setItem(k, JSON.stringify(obj)); } catch (_) {} }

  function fieldLabelFor(el) {
    try {
      // explicit label[for=...]
      if (el.id) {
        var doc = el.ownerDocument || document;
        var lab = doc.querySelector("label[for='" + el.id + "']");
        if (lab && lab.textContent) return lab.textContent.trim().toLowerCase();
      }
      // wrapping <label>
      var p = el.parentElement;
      while (p) {
        if (p.tagName === "LABEL" && p.textContent) return p.textContent.trim().toLowerCase();
        p = p.parentElement;
      }
    } catch (_) {}
    return "";
  }

  function fingerprint(el) {
    var parts = [];
    try {
      if (el.name) parts.push("name=" + el.name);
      if (el.id) parts.push("id=" + el.id);
      if (el.placeholder) parts.push("ph=" + el.placeholder);
      var a = el.getAttribute && el.getAttribute("aria-label");
      if (a) parts.push("aria=" + a);
      var t = el.type || el.tagName;
      if (t) parts.push("type=" + t);
    } catch (_) {}
    return parts.join("|").toLowerCase();
  }

  function classify(el) {
    // v10: tighter classification. The FaceAge questionnaire we observed in
    // production never asks for age, gender or skin tone, so we don't hunt
    // for them here. We only match on inputs that are CLEARLY name or email,
    // and only on actual <input> elements (not checkbox labels whose text
    // happens to contain the word "name").
    var tag = el.tagName;
    if (tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") return null;
    var fp = fingerprint(el);
    var lab = fieldLabelFor(el);
    var hay = fp + " " + lab;
    // Reject anything that smells like an opt-in checkbox label or marketing copy
    if (el.type === "checkbox") return null;
    if (/receive|newsletter|emails|subscribe|terms|consent|policy/.test(hay)) return null;
    // first_name: explicit field name / placeholder / aria, not just any "name" hit
    if (/first.?name|firstname|given.?name|placeholder=[^|]*name|aria=[^|]*name|ph=[^|]*name|name=name\b|name=\"name\"|id=name\b/.test(hay) && el.type !== "email") return "first_name";
    if (el.type === "text" && /\bname\b/.test(hay) && !/last|sur|user|business|company|brand|product/.test(hay)) return "first_name";
    if (el.type === "email" || /email|e-mail/.test(hay)) return "email";
    return null;
  }

  function valueOf(el) {
    try {
      if (el.type === "radio" || el.type === "checkbox") {
        if (!el.checked) return null;
        return (el.value && el.value !== "on") ? el.value : (el.labels && el.labels[0] && el.labels[0].textContent.trim()) || el.parentElement && el.parentElement.textContent && el.parentElement.textContent.trim() || el.value;
      }
      if (el.tagName === "SELECT") {
        return el.options && el.options[el.selectedIndex] ? (el.options[el.selectedIndex].value || el.options[el.selectedIndex].textContent.trim()) : "";
      }
      return (el.value != null ? String(el.value) : "").trim();
    } catch (_) { return null; }
  }

  function persistField(cat, raw) {
    if (raw == null || raw === "") return;
    var v = String(raw).trim();
    if (!v) return;
    var snap = lsGetJSON(Q_KEY);
    if (cat === "first_name") {
      // Sanity: real first names are short, no @, not pure digits, no sentence punctuation
      if (v.indexOf("@") !== -1) return;
      if (v.length > 40) return;
      if (/[.!?]\s|emails|subscribe|receive|newsletter|terms|consent|unsubscribe/i.test(v)) return;
      lsSet("ovssc3_first_name", v);
      snap.first_name = v;
    } else if (cat === "email") {
      if (v.indexOf("@") === -1) return;
      lsSet("ovssc3_email", v);
      snap.email = v;
    }
    snap._ts = Date.now();
    lsSetJSON(Q_KEY, snap);
  }

  function handleEl(el) {
    // v10: do not infer answers from data-value cards — too noisy. Only
    // handle real form inputs that can be classified as name/email.
    if (!el || el.nodeType !== 1) return;
    var cat = classify(el);
    if (!cat) return;
    var v = valueOf(el);
    if (v == null) return;
    persistField(cat, v);
  }

  function installListeners(doc, label) {
    if (!doc || doc.__ovssc_input_hook_installed) return false;
    try {
      var fn = function (e) {
        try { handleEl(e.target); } catch (_) {}
      };
      doc.addEventListener("input", fn, true);
      doc.addEventListener("change", fn, true);
      doc.addEventListener("keyup", fn, true);
      doc.addEventListener("blur", fn, true);
      doc.addEventListener("click", fn, true);
      doc.__ovssc_input_hook_installed = true;
      try { console.log("[ovssc-dom v9] input hook installed on " + label); } catch (_) {}
      // Also sweep any already-rendered fields once
      try {
        var nodes = doc.querySelectorAll("input, select, textarea, [data-value], [data-answer], [data-key]");
        for (var i = 0; i < nodes.length; i++) {
          try { handleEl(nodes[i]); } catch (_) {}
        }
      } catch (_) {}
      return true;
    } catch (e) {
      try { console.log("[ovssc-dom v9] hook install failed on " + label + ": " + e.message); } catch (_) {}
      return false;
    }
  }

  // Install on parent doc immediately
  installListeners(document, "parent");

  // Poll for iframe doc and install when ready; also re-install on src changes
  (function iframeHookLoop() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      try {
        var iframe = document.getElementById("FaceAge-module-iframe")
              || document.querySelector("#faceAge-module iframe")
              || document.querySelector("iframe[id*='FaceAge']")
              || document.querySelector("iframe[src*='faceage']")
              || document.querySelector("iframe[src*='FaceAge']");
        if (iframe) {
          var idoc = null;
          try { idoc = iframe.contentDocument; } catch (_) {}
          if (idoc) {
            // If the inner doc has been replaced, the flag is gone
            installListeners(idoc, "iframe");
          }
          // Also catch future doc replacements via load event
          if (!iframe.__ovssc_load_hooked) {
            try {
              iframe.addEventListener("load", function () {
                try { installListeners(iframe.contentDocument, "iframe(reload)"); } catch (_) {}
              });
              iframe.__ovssc_load_hooked = true;
            } catch (_) {}
          }
        }
      } catch (_) {}
      if (tries > 3000) { try { clearInterval(iv); } catch (_) {} }
    }, 100);
  })();

  /* End v9 input-hook */

  var KEY_MAP = {
    fineWrinkles: "FineLines",
    eyeWrinkles: "EyeAreaLines",
    deepWrinkles: "EstablishedLines",
    darkCircle: "UnderEyeDarkness",
    eyeBag: "UnderEyePuffiness",
    pores: "PoreVisibility",
    pigment: "UnevenTone",
    redness: "VisibleRedness",
    oiliness: "SurfaceOil",
    dryness: "LackOfMoisture",
    sagginess: "LossOfFirmness",
    acne: "BlemishActivity",
  };
  var CONCERN_KEYS = Object.keys(KEY_MAP);

  // Reverse: FaceAge canonical key → our canonical key (used when reading
  // formData.skinAnalysis which uses fineWrinkles etc.)
  var REVERSE_MAP = {};
  Object.keys(KEY_MAP).forEach(function (k) { REVERSE_MAP[k] = KEY_MAP[k]; });

  // Tunables
  var SETTLE_MS = 600;
  var TAB_WAIT_MS = 360;
  var TAB_WAIT_RETRY_MS = 620;
  var POLL_INTERVAL_MS = 1000;
  var POLL_MAX = 360;
  var SECOND_SWEEP_MIN_COUNT = 8;

  function log(msg, level) {
    try { console.log("[ovssc-dom v8] " + (level || "info") + ": " + msg); } catch (_) {}
  }

  function findFrame() {
    var c = [
      document.getElementById("FaceAge-module-iframe"),
      document.querySelector("#faceAge-module iframe"),
      document.querySelector("iframe[id*='FaceAge']"),
      document.querySelector("iframe[src*='faceage']"),
      document.querySelector("iframe[src*='FaceAge']"),
    ].filter(Boolean);
    return c[0] || null;
  }

  function parsePercent(el) {
    if (!el) return null;
    var txt = (el.textContent || "").trim();
    var m = txt.match(/(\d+(?:\.\d+)?)\s*%?/);
    if (!m) return null;
    var n = parseFloat(m[1]);
    return isNaN(n) ? null : n;
  }

  /* ---------------- FaceAge instance globals walker (v7) ----------------
   * The older ovssc3-bridge-v1 finds the FaceAge instance by walking
   * window globals for an object whose .w.globals or .globals exposes
   * formData + skinAnalysis + skinAge + skinTone + gender + firstName.
   * We do the same on both the parent window and the iframe window. */
  function walkForFaceAgeGlobals(win) {
    if (!win) return null;
    try {
      // Direct hit on a known global if FaceAge exposes one
      if (win.faceAgeData && typeof win.faceAgeData === "object") {
        return { formData: win.faceAgeData, globals: win.faceAgeData, source: "win.faceAgeData" };
      }
      if (win.FaceAge && win.FaceAge.globals) {
        return { formData: win.FaceAge.globals.formData || {}, globals: win.FaceAge.globals, source: "win.FaceAge.globals" };
      }
    } catch (_) {}

    // Walk own properties looking for { w: { globals: { formData } } } shape
    // used by the bundled FaceAge widget.
    var keys;
    try { keys = Object.keys(win); } catch (_) { keys = []; }
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var v;
      try { v = win[k]; } catch (_) { continue; }
      if (!v || typeof v !== "object") continue;
      // shape 1: v.w.globals.formData
      try {
        if (v.w && v.w.globals && (v.w.globals.formData || v.w.globals.firstName || v.w.globals.skinAnalysis || v.w.globals.skinAnalyzeData)) {
          return { formData: v.w.globals.formData || {}, globals: v.w.globals, source: "win." + k + ".w.globals" };
        }
      } catch (_) {}
      // shape 2: v.globals.formData
      try {
        if (v.globals && (v.globals.formData || v.globals.firstName || v.globals.skinAnalysis || v.globals.skinAnalyzeData)) {
          return { formData: v.globals.formData || {}, globals: v.globals, source: "win." + k + ".globals" };
        }
      } catch (_) {}
    }
    return null;
  }

  /* Merge instance globals from parent + iframe windows.
     v8: consult the ctor-patched instance FIRST. That's the only reliable
     handle on prod. Then fall back to v7's window-enum walker. */
  function readFaceAgeInstance() {
    var out = { formData: {}, globals: {}, sources: [] };

    // v10: check BOTH instance globals. The other bridge (ovssc3-bridge-v1) may
    // win the ctor patch race — in which case window.__ovssc_faceAge_instance is
    // null but window.__ovssc3_faceAgeInstance is populated.
    function ingest(inst, label) {
      try {
        if (!inst || !inst.w || !inst.w.globals) return;
        var g = inst.w.globals;
        var fd = g.formData || {};
        Object.keys(fd).forEach(function (k) {
          if (fd[k] != null && fd[k] !== "" && out.formData[k] == null) out.formData[k] = fd[k];
        });
        Object.keys(g).forEach(function (k) {
          if (k === "formData") return;
          if (g[k] != null && g[k] !== "" && out.globals[k] == null) out.globals[k] = g[k];
        });
        out.sources.push(label);
      } catch (_) {}
    }
    try {
      ingest(window.__ovssc_faceAge_instance, "instance.v9[" + (window.__ovssc_faceAge_instance_source || "ctor") + "]");
      ingest(window.__ovssc3_faceAgeInstance, "instance.ovssc3");
    } catch (_) {}

    // v7 fallback: window-enum walker on parent + iframe
    var iframe = findFrame();
    var iwin = null;
    try { iwin = iframe && iframe.contentWindow; } catch (_) {}

    [window, iwin].forEach(function (w) {
      var found = walkForFaceAgeGlobals(w);
      if (found) {
        Object.keys(found.formData || {}).forEach(function (k) {
          if (found.formData[k] != null && found.formData[k] !== "" && out.formData[k] == null) {
            out.formData[k] = found.formData[k];
          }
        });
        Object.keys(found.globals || {}).forEach(function (k) {
          if (found.globals[k] != null && found.globals[k] !== "" && k !== "formData" && out.globals[k] == null) {
            out.globals[k] = found.globals[k];
          }
        });
        out.sources.push(found.source);
      }
    });

    return out;
  }

  function readSkinAgeAndHealth(doc) {
    var out = { skin_age: null, skin_health: null };
    var nodes = doc.querySelectorAll('div[style*="position: absolute"], div[style*="position:absolute"]');
    for (var i = 0; i < nodes.length; i++) {
      var t = nodes[i].textContent || "";
      if (out.skin_health === null) {
        var mh = t.match(/Skin\s*Health\s*:?\s*(\d+(?:\.\d+)?)\s*%?/i);
        if (mh) out.skin_health = parseFloat(mh[1]);
      }
      if (out.skin_age === null) {
        var ma = t.match(/Skin\s*Age\s*:?\s*(\d+)/i);
        if (ma) out.skin_age = parseInt(ma[1], 10);
      }
      if (out.skin_age !== null && out.skin_health !== null) break;
    }
    if (out.skin_age === null || out.skin_health === null) {
      var all = doc.body ? doc.body.innerText || "" : "";
      if (out.skin_age === null) {
        var ma2 = all.match(/Skin\s*Age[^\d]{0,10}(\d{2})/i);
        if (ma2) out.skin_age = parseInt(ma2[1], 10);
      }
      if (out.skin_health === null) {
        var mh2 = all.match(/Skin\s*Health[^\d]{0,10}(\d{1,3})/i);
        if (mh2) out.skin_health = parseFloat(mh2[1]);
      }
    }
    return out;
  }

  function readFindingsFromDom(doc) {
    var out = { findings: {}, skin_age: null, skin_health: null, warning: null };
    Object.keys(KEY_MAP).forEach(function (faKey) {
      var selectors = [
        ".point_analysis." + CSS.escape(faKey),
        ".point_analysis[data-key='" + faKey + "']",
        "[data-point='" + faKey + "'] .percent",
        "[data-point='" + faKey + "']",
      ];
      var el = null;
      for (var si = 0; si < selectors.length; si++) {
        try { el = doc.querySelector(selectors[si]); } catch (_) { el = null; }
        if (el) break;
      }
      if (el) {
        var v = parsePercent(el);
        if (v !== null) out.findings[KEY_MAP[faKey]] = v;
      }
    });
    var sah = readSkinAgeAndHealth(doc);
    out.skin_age = sah.skin_age;
    out.skin_health = sah.skin_health;
    var warn = doc.querySelector(".warning_image");
    if (warn) out.warning = (warn.textContent || "").trim();
    return out;
  }

  /* v7: read findings directly from FaceAge's skinAnalysis object (the
     engine output) so we get all 12 metrics even when the DOM tab list
     has not rendered them. */
  function readFindingsFromInstance(instance) {
    var findings = {};
    if (!instance || !instance.globals) return findings;
    var g = instance.globals;
    // v8: ovssc3-bridge confirms FaceAge exposes findings at g.skinAnalyzeData.analysis
    // (note the 'D' — skinAnalyze**Data**, not skinAnalysis). Also try the v7 paths
    // for backward compatibility.
    var candidates = [];
    try { if (g.skinAnalyzeData && g.skinAnalyzeData.analysis) candidates.push(g.skinAnalyzeData.analysis); } catch (_) {}
    try { if (g.skinAnalyzeData && g.skinAnalyzeData.problems) candidates.push(g.skinAnalyzeData.problems); } catch (_) {}
    try { if (g.skinAnalysis) candidates.push(g.skinAnalysis); } catch (_) {}
    try { if (g.analysis) candidates.push(g.analysis); } catch (_) {}
    try { if (g.results) candidates.push(g.results); } catch (_) {}
    for (var ci = 0; ci < candidates.length; ci++) {
      var src = candidates[ci];
      if (!src) continue;
      if (Array.isArray(src)) {
        src.forEach(function (entry) {
          if (!entry) return;
          var k = entry.key || entry.name || entry.id;
          // FaceAge uses .number for the score, ovssc3-bridge reads that. Also try value/percentage/score.
          var v = entry.number != null ? entry.number
                : (entry.value != null ? entry.value
                : (entry.percentage != null ? entry.percentage : entry.score));
          if (k && KEY_MAP[k] && typeof v !== "undefined") {
            var n = parseFloat(v);
            if (isFinite(n) && (findings[KEY_MAP[k]] == null || n > findings[KEY_MAP[k]])) {
              findings[KEY_MAP[k]] = n;
            }
          }
        });
      } else if (typeof src === "object") {
        Object.keys(src).forEach(function (k) {
          var v = src[k];
          if (KEY_MAP[k] && typeof v !== "undefined") {
            var n = parseFloat(v);
            if (isFinite(n) && (findings[KEY_MAP[k]] == null || n > findings[KEY_MAP[k]])) {
              findings[KEY_MAP[k]] = n;
            }
          }
        });
      }
    }
    return findings;
  }

  function topConcern(findings) {
    var rank = ["EstablishedLines","VisibleRedness","UnderEyeDarkness","PoreVisibility","UnevenTone","SurfaceOil","LackOfMoisture","LossOfFirmness","BlemishActivity","FineLines","EyeAreaLines","UnderEyePuffiness"];
    var best = null, bestVal = -1;
    rank.forEach(function (k) {
      var v = findings[k];
      if (typeof v === "number" && v > bestVal) { bestVal = v; best = k; }
    });
    return best;
  }

  function top3Concerns(findings) {
    var entries = [];
    Object.keys(findings).forEach(function (k) {
      var v = findings[k];
      if (typeof v === "number") entries.push({ concern_key: k, value: v });
    });
    entries.sort(function (a, b) { return b.value - a.value; });
    return entries.slice(0, 3);
  }

  function resultsReady(doc) {
    if (!doc) return false;
    var nodes = doc.querySelectorAll(".point_analysis, [data-point]");
    if (!nodes.length) return false;
    var hits = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (parsePercent(nodes[i]) !== null) hits++;
      if (hits >= 1) return true;
    }
    return false;
  }

  function findMainCanvas(doc) {
    var c = doc.getElementById("face_age_analyzer_canvas_server")
         || doc.querySelector("canvas.face_age_analyzer_canvas_server")
         || doc.querySelector("canvas[id*='analyzer']")
         || doc.querySelector("canvas[id*='face_age']");
    if (c) return c;
    var canvases = doc.querySelectorAll("canvas");
    var best = null, bestArea = 0;
    for (var i = 0; i < canvases.length; i++) {
      var ca = canvases[i];
      var area = (ca.width || 0) * (ca.height || 0);
      if (area > bestArea) { best = ca; bestArea = area; }
    }
    return best;
  }

  function safeDataURL(canvas, quality) {
    try { return canvas.toDataURL("image/jpeg", quality || 0.85); }
    catch (e) { return null; }
  }

  function dataURLFingerprint(s) {
    if (!s || typeof s !== "string") return "0";
    var n = s.length;
    return [s.length, s.slice(64, 96), s.slice(n>>2, (n>>2)+32), s.slice(n>>1, (n>>1)+32), s.slice(n-96, n-64)].join("|");
  }

  function activateTab(doc, key) {
    var el = null;
    var selectors = [
      ".point_analysis." + CSS.escape(key),
      ".point_analysis[data-key='" + key + "']",
      "[data-point='" + key + "']",
    ];
    for (var si = 0; si < selectors.length; si++) {
      try { el = doc.querySelector(selectors[si]); } catch (_) { el = null; }
      if (el) break;
    }
    if (!el) return null;
    try { el.click(); } catch (_) {}
    try {
      var inner = el.querySelector("button, [role='button'], a, .tab");
      if (inner && inner !== el) inner.click();
    } catch (_) {}
    var win = doc.defaultView || window;
    ["pointerdown","mousedown","mouseup","click","pointerup"].forEach(function (etype) {
      try {
        var EventCtor = win.MouseEvent || MouseEvent;
        el.dispatchEvent(new EventCtor(etype, { bubbles: true, cancelable: true, view: win }));
      } catch (_) {}
    });
    try {
      var actives = doc.querySelectorAll(".point_analysis.active");
      for (var ai = 0; ai < actives.length; ai++) actives[ai].classList.remove("active");
      el.classList.add("active");
    } catch (_) {}
    return el;
  }

  function findActiveKey(doc) {
    var el = doc.querySelector(".point_analysis.active, [data-point].active");
    if (!el) return null;
    return el.getAttribute("data-key") || el.getAttribute("data-point") || null;
  }

  function sweepConcerns(doc, tabWaitMs) {
    return new Promise(function (resolve) {
      var canvas = findMainCanvas(doc);
      if (!canvas) { resolve({ concerns: {}, main: null, fingerprints: {} }); return; }
      var originalKey = findActiveKey(doc) || "skin_info";
      var concerns = {};
      var fingerprints = {};
      var idx = 0;
      var main = safeDataURL(canvas, 0.88);
      function step() {
        if (idx >= CONCERN_KEYS.length) {
          activateTab(doc, originalKey);
          setTimeout(function () {
            resolve({ concerns: concerns, main: main, fingerprints: fingerprints });
          }, 200);
          return;
        }
        var key = CONCERN_KEYS[idx++];
        var activated = activateTab(doc, key);
        if (!activated) { step(); return; }
        setTimeout(function () {
          var url = safeDataURL(canvas, 0.82);
          var fp = dataURLFingerprint(url);
          if (url) {
            concerns[key] = url;
            fingerprints[key] = fp;
          }
          step();
        }, tabWaitMs || TAB_WAIT_MS);
      }
      step();
    });
  }

  function getFaceImage(doc) {
    try {
      var ls = localStorage.getItem("ovssc3_capturedFace");
      if (ls && ls.indexOf("data:image") === 0) return ls;
    } catch (_) {}
    var c = findMainCanvas(doc);
    return c ? safeDataURL(c, 0.92) : null;
  }

  /* v7: full questionnaire read — instance formData FIRST, then ls, then DOM. */
  function readQuestionnaireRaw(doc, instance) {
    var q = {};
    var fd = (instance && instance.formData) || {};

    // Path 1: FaceAge instance formData (highest authority — this is what the
    // user typed/selected in the FaceAge questionnaire)
    if (fd.firstName) q.first_name = fd.firstName;
    if (fd.first_name && !q.first_name) q.first_name = fd.first_name;
    if (fd.name && !q.first_name) q.first_name = fd.name;
    // v10: FaceAge uses dynamic ids like text_input_18597 for free-text fields.
    // The questionnaire only has ONE free-text input (the name), so if firstName
    // is unset, take the FIRST short non-email string value we see on a key that
    // looks like a text input.
    if (!q.first_name) {
      var textKeys = Object.keys(fd).filter(function(k){ return /text_input|name|firstName/i.test(k); });
      for (var i = 0; i < textKeys.length; i++) {
        var v = fd[textKeys[i]];
        if (typeof v === "string" && v.length > 0 && v.length < 60 && v.indexOf("@") === -1 && !/^[0-9]+$/.test(v)) {
          q.first_name = v;
          break;
        }
      }
    }
    // Also check globals.firstName (FaceAge sometimes promotes the typed name to globals)
    if (!q.first_name && instance && instance.globals && instance.globals.firstName) q.first_name = instance.globals.firstName;
    if (fd.email) q.email = fd.email;
    if (fd.age != null) q.age = parseInt(fd.age, 10) || fd.age;
    if (fd.gender) q.gender = String(fd.gender).toLowerCase();
    if (fd.skinTone) q.skin_tone = String(fd.skinTone).toLowerCase();
    if (fd.skin_tone && !q.skin_tone) q.skin_tone = String(fd.skin_tone).toLowerCase();
    if (fd.concerns) q.concerns = fd.concerns;
    if (!q.concerns && fd.skincare) q.concerns = fd.skincare; // FaceAge uses 'skincare' for the concerns list
    // Lifestyle: forward every formData entry except the canonical ones above
    // and except the dynamic text-input key we already used for first_name.
    var skip = { firstName:1, first_name:1, name:1, email:1, age:1, gender:1, skinTone:1, skin_tone:1, concerns:1 };
    q.lifestyle = q.lifestyle || {};
    Object.keys(fd).forEach(function (k) {
      if (skip[k]) return;
      var v = fd[k];
      if (v == null || v === "" || typeof v === "function") return;
      q.lifestyle[k] = v;
    });

    // Path 2: ovssc3_reading blob (merge any field instance didn't have)
    try {
      var raw = localStorage.getItem("ovssc3_reading");
      if (raw) {
        var parsed = JSON.parse(raw);
        if (!q.first_name && (parsed.first_name || parsed.firstName)) q.first_name = parsed.first_name || parsed.firstName;
        if (!q.email && parsed.email) q.email = parsed.email;
        if (!q.age && parsed.age) q.age = parsed.age;
        if (!q.gender && parsed.gender) q.gender = parsed.gender;
        if (!q.skin_tone && (parsed.skin_tone || parsed.skinTone)) q.skin_tone = parsed.skin_tone || parsed.skinTone;
        if (parsed.lifestyle) {
          Object.keys(parsed.lifestyle).forEach(function (k) {
            if (!q.lifestyle[k]) q.lifestyle[k] = parsed.lifestyle[k];
          });
        }
      }
    } catch (_) {}

    // Path 2b (v9): ovssc3_questionnaire snapshot written by input-hook
    try {
      var qraw = localStorage.getItem("ovssc3_questionnaire");
      if (qraw) {
        var qp = JSON.parse(qraw);
        if (qp && typeof qp === "object") {
          if (!q.first_name && qp.first_name) q.first_name = qp.first_name;
          if (!q.email && qp.email) q.email = qp.email;
          if (!q.age && qp.age) q.age = qp.age;
          if (!q.gender && qp.gender) q.gender = qp.gender;
          if (!q.skin_tone && qp.skin_tone) q.skin_tone = qp.skin_tone;
          if (qp.lifestyle) {
            Object.keys(qp.lifestyle).forEach(function (k) {
              if (!q.lifestyle[k]) q.lifestyle[k] = qp.lifestyle[k];
            });
          }
        }
      }
    } catch (_) {}

    // Path 3: standalone keys
    try {
      var fn = localStorage.getItem("ovssc3_first_name");
      var em = localStorage.getItem("ovssc3_email");
      var ag = localStorage.getItem("ovssc3_age");
      var gn = localStorage.getItem("ovssc3_gender");
      var st = localStorage.getItem("ovssc3_skin_tone");
      if (fn && !q.first_name) q.first_name = fn;
      if (em && !q.email) q.email = em;
      if (ag && !q.age) q.age = parseInt(ag, 10) || ag;
      if (gn && !q.gender) q.gender = gn;
      if (st && !q.skin_tone) q.skin_tone = st;
    } catch (_) {}

    // Path 4: FaceAge iframe DOM input scrape (last resort)
    if (doc) {
      try {
        if (!q.first_name) {
          var nameInput = doc.querySelector(
            "input[name='first_name'], input[name='firstName'], input[name='name'], " +
            "input[id*='first_name' i], input[id*='firstname' i], input[id*='name' i], " +
            "input[placeholder*='first name' i], input[placeholder*='your name' i]"
          );
          if (nameInput && nameInput.value) q.first_name = nameInput.value.trim();
        }
        if (!q.email) {
          var emInput = doc.querySelector("input[type='email'], input[name='email'], input[name='Email']");
          if (emInput && emInput.value) q.email = emInput.value.trim();
        }
        if (!q.age) {
          var ageInput = doc.querySelector("input[name='age' i], input[id*='age' i]:not([id*='manage' i])");
          if (ageInput && ageInput.value) q.age = parseInt(ageInput.value, 10) || ageInput.value;
        }
        if (!q.gender) {
          var gRadios = doc.querySelectorAll("input[type='radio'][name*='gender' i]:checked");
          if (gRadios.length) q.gender = (gRadios[0].value || "").toLowerCase() || null;
        }
        if (!q.skin_tone) {
          var stRadios = doc.querySelectorAll("input[type='radio'][name*='skin' i][name*='tone' i]:checked, input[type='radio'][name*='tone' i]:checked");
          if (stRadios.length) q.skin_tone = (stRadios[0].value || "").toLowerCase();
        }
      } catch (e) {
        log("DOM scrape failed: " + e.message, "warn");
      }
    }
    return q;
  }

  /* v7: MERGE with the existing ovssc3_reading rather than overwriting.
     A blank value from this run must never blank a populated value from
     the previous bridge run. */
  function persistReading(data, image, questionnaire, sweep) {
    var concerns = (sweep && sweep.concerns) || {};
    var main = (sweep && sweep.main) || image;

    var existing = {};
    try {
      var raw = localStorage.getItem("ovssc3_reading");
      if (raw) existing = JSON.parse(raw) || {};
    } catch (_) { existing = {}; }

    function pick(newV, oldV) {
      if (newV === null || newV === undefined || newV === "") return oldV != null && oldV !== "" ? oldV : newV;
      return newV;
    }
    function mergeFindings(a, b) {
      var out = {};
      Object.keys(b || {}).forEach(function (k) { out[k] = b[k]; });
      Object.keys(a || {}).forEach(function (k) {
        if (typeof a[k] === "number" && a[k] > 0) out[k] = a[k];
      });
      return out;
    }

    var first_name = pick(questionnaire.first_name, existing.first_name || existing.firstName);
    var age = pick(questionnaire.age, existing.age);
    var gender = pick(questionnaire.gender, existing.gender);
    var skin_tone = pick(questionnaire.skin_tone, existing.skin_tone || existing.skinTone);
    var email = pick(questionnaire.email, existing.email);

    var mergedFindings = mergeFindings(data.findings || {}, existing.findings || existing.detected || {});
    var mergedLifestyle = {};
    Object.keys(existing.lifestyle || {}).forEach(function (k) { mergedLifestyle[k] = existing.lifestyle[k]; });
    Object.keys(questionnaire.lifestyle || {}).forEach(function (k) {
      if (questionnaire.lifestyle[k] != null && questionnaire.lifestyle[k] !== "") {
        mergedLifestyle[k] = questionnaire.lifestyle[k];
      }
    });

    var reading = {
      first_name: first_name || "",
      firstName: first_name || "",
      email: email || "",
      age: age || null,
      gender: gender || null,
      skin_tone: skin_tone || null,
      skinTone: skin_tone || null,
      concerns: questionnaire.concerns || existing.concerns || [],
      lifestyle: mergedLifestyle,
      findings: mergedFindings,
      detected: mergedFindings,
      top3: top3Concerns(mergedFindings),
      top_concern: topConcern(mergedFindings),
      skin_age: pick(data.skin_age, existing.skin_age || existing.skinAge),
      skinAge: pick(data.skin_age, existing.skin_age || existing.skinAge),
      skin_health: pick(data.skin_health, existing.skin_health || existing.skinHealth),
      skinHealth: pick(data.skin_health, existing.skin_health || existing.skinHealth),
      warning: data.warning || existing.warning || null,
      face_image: image || existing.face_image || existing.faceImage || null,
      faceImage: image || existing.face_image || existing.faceImage || null,
      overlays_meta: {
        source: "canvas_tab_sweep_v7",
        count: Object.keys(concerns).length,
        keys: Object.keys(concerns),
      },
      _source: "skinread_bridge_dom_v9",
      _trigger: "tryCapture",
      _captureSource: "iframe_canvas_tabs",
      _ts: Date.now(),
      _bridge_version: window.__ovssc_dom_bridge_version,
    };

    try { localStorage.setItem("ovssc3_reading", JSON.stringify(reading)); }
    catch (e) { log("ls.setItem reading failed: " + e.message, "err"); }

    try {
      if (reading.first_name) localStorage.setItem("ovssc3_first_name", reading.first_name);
      if (reading.email)      localStorage.setItem("ovssc3_email", reading.email);
      if (reading.age != null) localStorage.setItem("ovssc3_age", String(reading.age));
      if (reading.gender)     localStorage.setItem("ovssc3_gender", reading.gender);
      if (reading.skin_tone)  localStorage.setItem("ovssc3_skin_tone", reading.skin_tone);
    } catch (_) {}

    if (image) { try { localStorage.setItem("ovssc3_capturedFace", image); } catch (_) {} }
    if (main)  { try { localStorage.setItem("ovssc3_capturedMain", main); } catch (_) {} }

    if (concerns && Object.keys(concerns).length) {
      try {
        localStorage.setItem("ovssc3_capturedConcerns", JSON.stringify(concerns));
      } catch (e) {
        log("ls.setItem concerns failed: " + e.message + " — falling back to per-key", "warn");
        Object.keys(concerns).forEach(function (k) {
          try { localStorage.setItem("ovssc3_concern_" + k, concerns[k]); } catch (_) {}
        });
      }
    }

    var overlaysLegacy = {};
    Object.keys(concerns).forEach(function (k) {
      overlaysLegacy[k] = {
        key: k,
        masks: concerns[k],
        areas: null,
        isSVG: false,
        showInOverview: true,
      };
    });
    if (Object.keys(overlaysLegacy).length) {
      try { localStorage.setItem("ovssc3_overlays", JSON.stringify(overlaysLegacy)); }
      catch (e) { log("ls.setItem overlays failed: " + e.message, "err"); }
    }

    return reading;
  }

  function dispatchReady(reading, concerns) {
    try {
      window.dispatchEvent(new CustomEvent("ovssc:findings-ready", {
        detail: {
          findings: reading.findings,
          skin_age: reading.skin_age,
          skin_health: reading.skin_health,
          warning: reading.warning,
          has_image: !!reading.face_image,
        },
      }));
    } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("ovssc3:ready", { detail: reading })); } catch (_) {}
    if (concerns && Object.keys(concerns).length) {
      try {
        window.dispatchEvent(new CustomEvent("ovssc3:concerns-ready", {
          detail: { count: Object.keys(concerns).length, keys: Object.keys(concerns) },
        }));
      } catch (_) {}
    }
  }

  var captured = null;
  var inFlight = false;
  var settleArmedAt = 0;

  function tryCapture() {
    if (inFlight || captured) return false;
    var iframe = findFrame();
    if (!iframe) return false;
    var doc;
    try { doc = iframe.contentDocument; } catch (_) { return false; }
    if (!doc || !resultsReady(doc)) {
      settleArmedAt = 0;
      return false;
    }
    var now = Date.now();
    if (!settleArmedAt) {
      settleArmedAt = now;
      log("Results ready — settling for " + SETTLE_MS + "ms");
      return false;
    }
    if (now - settleArmedAt < SETTLE_MS) return false;

    // v7: read FaceAge instance globals FIRST so we have formData when we
    // build the questionnaire.
    var instance = readFaceAgeInstance();
    log("Instance harvest sources: " + (instance.sources.join(", ") || "(none — falling back to DOM only)"));

    var data = readFindingsFromDom(doc);
    // v7: also merge findings from instance.globals.skinAnalysis
    var instanceFindings = readFindingsFromInstance(instance);
    Object.keys(instanceFindings).forEach(function (k) {
      if (!(k in data.findings)) data.findings[k] = instanceFindings[k];
    });
    if (Object.keys(data.findings).length < 1) return false;

    // Pull skin_age/skin_health from instance if DOM did not see them
    if (data.skin_age == null && instance.globals.skinAge != null) data.skin_age = Math.round(instance.globals.skinAge);
    if (data.skin_health == null && instance.globals.damagePercentage != null) {
      data.skin_health = Math.max(0, Math.min(100, 100 - instance.globals.damagePercentage));
    }

    inFlight = true;
    var image = getFaceImage(doc);
    var quest = readQuestionnaireRaw(doc, instance);

    sweepConcerns(doc, TAB_WAIT_MS).then(function (sweep1) {
      var count1 = Object.keys(sweep1.concerns).length;
      log("Sweep 1: " + count1 + " concerns captured (retry threshold: " + SECOND_SWEEP_MIN_COUNT + ")");
      if (count1 >= SECOND_SWEEP_MIN_COUNT) return Promise.resolve(sweep1);
      log("Sweep 1 below threshold — running retry with " + TAB_WAIT_RETRY_MS + "ms wait");
      return sweepConcerns(doc, TAB_WAIT_RETRY_MS).then(function (sweep2) {
        var count2 = Object.keys(sweep2.concerns).length;
        log("Sweep 2: " + count2 + " concerns captured (picking better)");
        return count2 >= count1 ? sweep2 : sweep1;
      });
    }).then(function (sweep) {
      var reading = persistReading(data, image, quest, sweep);
      captured = reading;
      window.__ovssc_captured = {
        findings: reading.findings,
        findings_count: Object.keys(reading.findings || {}).length,
        skin_age: reading.skin_age,
        skin_health: reading.skin_health,
        warning: reading.warning,
        has_image: !!reading.face_image,
        first_name: reading.first_name,
        email: reading.email,
        age: reading.age,
        gender: reading.gender,
        skin_tone: reading.skin_tone,
        overlays_meta: reading.overlays_meta,
        instance_sources: instance.sources,
      };
      log("Captured · findings=" + Object.keys(reading.findings).length +
          " age=" + reading.skin_age +
          " concerns=" + Object.keys(sweep.concerns).length +
          " name='" + (reading.first_name || "") + "'", "ok");
      dispatchReady(reading, sweep.concerns);
      inFlight = false;
    }).catch(function (e) {
      log("capture failed: " + e.message, "err");
      var reading = persistReading(data, image, quest, { concerns: {}, main: image });
      captured = reading;
      dispatchReady(reading, {});
      inFlight = false;
    });
    return true;
  }

  var pollCount = 0;
  var pollTimer = setInterval(function () {
    pollCount++;
    if (pollCount > POLL_MAX) { clearInterval(pollTimer); log("Poll timed out without capture"); return; }
    if (captured) return;
    tryCapture();
  }, POLL_INTERVAL_MS);

  window.__ovssc_dom_state = function () {
    function has(k) {
      try {
        var v = localStorage.getItem(k);
        if (v === null || v === undefined) return { present: false };
        var len = (typeof v === "string") ? v.length : 0;
        return { present: true, length: len, preview: (typeof v === "string" ? v.slice(0, 64) : null) };
      } catch (e) { return { present: false, error: e.message }; }
    }
    return {
      version: window.__ovssc_dom_bridge_version,
      captured: !!captured,
      in_flight: inFlight,
      poll_count: pollCount,
      settle_armed_at: settleArmedAt,
      captured_summary: window.__ovssc_captured || null,
      storage: {
        ovssc3_reading:           has("ovssc3_reading"),
        ovssc3_first_name:        has("ovssc3_first_name"),
        ovssc3_email:             has("ovssc3_email"),
        ovssc3_questionnaire:     has("ovssc3_questionnaire"),
        ovssc3_age:               has("ovssc3_age"),
        ovssc3_gender:            has("ovssc3_gender"),
        ovssc3_skin_tone:         has("ovssc3_skin_tone"),
        ovssc3_capturedFace:      has("ovssc3_capturedFace"),
        ovssc3_capturedMain:      has("ovssc3_capturedMain"),
        ovssc3_capturedConcerns:  has("ovssc3_capturedConcerns"),
        ovssc3_overlays:          has("ovssc3_overlays"),
      },
    };
  };

  window.__ovssc_dom_capture = function () {
    settleArmedAt = settleArmedAt || (Date.now() - SETTLE_MS - 1);
    var ok = tryCapture();
    log("Manual capture: " + (ok ? "ok (async)" : "not ready"));
    return window.__ovssc_captured;
  };

  log("Bridge v9 booted · " + window.__ovssc_dom_bridge_version);
})();
